import { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Check, Pencil, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { listItemVariants } from './motion';
import type { FaqEntry } from '../mockKnowledge';

interface FaqItemProps {
  faq: FaqEntry;
  onUpdate: (updates: Partial<FaqEntry>) => void;
  onDelete: () => void;
  onToggleInKnowledgeBase: () => void;
}

export function FaqItem({ faq, onUpdate, onDelete, onToggleInKnowledgeBase }: FaqItemProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ question: faq.question, answer: faq.answer });

  const startEditing = () => {
    setDraft({ question: faq.question, answer: faq.answer });
    setEditing(true);
  };

  const handleSave = () => {
    if (!draft.question.trim()) return;
    onUpdate({ question: draft.question.trim(), answer: draft.answer.trim() });
    setEditing(false);
    toast.success('FAQ updated');
  };

  return (
    <motion.li
      layout
      variants={listItemVariants}
      exit="exit"
      className="rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 shadow-2xs transition-all hover:border-slate-300 dark:hover:border-slate-700"
    >
      {editing ? (
        <div className="space-y-2">
          <Input
            value={draft.question}
            onChange={(event) => setDraft((prev) => ({ ...prev, question: event.target.value }))}
            aria-label="Question or topic"
            placeholder="Question / topic"
            className="h-8 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-xs text-slate-900 dark:text-slate-100 focus:border-primary/40 focus:ring-primary/20 shadow-2xs"
          />
          <Textarea
            value={draft.answer}
            onChange={(event) => setDraft((prev) => ({ ...prev, answer: event.target.value }))}
            aria-label="Answer"
            placeholder="Answer"
            rows={2}
            className="rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-xs text-slate-900 dark:text-slate-100 focus:border-primary/40 focus:ring-primary/20 shadow-2xs leading-relaxed"
          />
          <div className="flex justify-end gap-1.5">
            <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-7 px-3 rounded-full text-xs">
              <X className="mr-1 h-3.5 w-3.5" />
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={handleSave} className="h-7 px-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-xs">
              <Check className="mr-1 h-3.5 w-3.5" />
              Save
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-bold text-slate-900 dark:text-slate-100">{faq.question}</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{faq.answer}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <Checkbox
                checked={faq.inKnowledgeBase}
                onChange={onToggleInKnowledgeBase}
                aria-label={`Include "${faq.question}" in AI knowledge`}
              />
              <span className={cn('text-[11px] font-medium', faq.inKnowledgeBase ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500')}>
                {faq.inKnowledgeBase ? 'In AI' : 'Off'}
              </span>
            </label>
            <button
              type="button"
              onClick={startEditing}
              aria-label={`Edit "${faq.question}"`}
              className="rounded-lg p-1.5 text-slate-400 dark:text-slate-500 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={onDelete}
              aria-label={`Delete "${faq.question}"`}
              className="rounded-lg p-1.5 text-slate-400 dark:text-slate-500 transition-colors hover:bg-rose-50 dark:hover:bg-rose-950/50 hover:text-rose-600 dark:hover:text-rose-400"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </motion.li>
  );
}

export default FaqItem;
